import type { ProjectChannelFlags } from "~/lib/team-member-notifications";

import { isChannelId } from "./channel-catalog";
import type { ChannelId } from "./types";

export const SITEGLUE_CHANNELS_HEADER = "SiteGlue-Channels";

/** Default channels for the SiteGlue.ai and EasyGPT.builders marketing widgets. */
export const MARKETING_SITE_WIDGET_CHANNELS: ChannelId[] = [
  "website",
  "phone",
  "sms",
  "email",
  "scheduling",
  "video",
];

const CHANNEL_ALIASES: Record<string, ChannelId> = {
  website: "website",
  "website chat": "website",
  chat: "website",
  phone: "phone",
  "phone / front desk": "phone",
  "front desk": "phone",
  call: "phone",
  sms: "sms",
  text: "sms",
  "text messages": "sms",
  email: "email",
  scheduling: "scheduling",
  "online scheduling": "scheduling",
  video: "video",
  "video calls": "video",
  browser_call: "browser_call",
  "browser call": "browser_call",
  "browser calls": "browser_call",
  whatsapp: "whatsapp",
  "whats app": "whatsapp",
  messenger: "messenger",
  "facebook messenger": "messenger",
  "fb messenger": "messenger",
  social: "social",
  "social media": "social",
};

const HEADER_RE = /SiteGlue-Channels:\s*([^\n<#]+)/gi;
const WIDGET_ID_RE =
  /(?:easygpt\.builders|siteglue\.ai)\/w(?:-v2)?\.js\/?\?[^"'>\s]*\bid=([a-zA-Z0-9]+)/gi;
const WIDGET_ID_FALLBACK_RE = /\/w(?:-v2)?\.js\/?\?[^"'>\s]*\bid=([a-zA-Z0-9]+)/gi;

export function renderSiteGlueChannelsLine(channels: readonly string[]): string {
  const unique = uniqueChannelIds(channels);
  return `${SITEGLUE_CHANNELS_HEADER}: ${unique.join(", ")}`;
}

export function renderSiteGlueChannelsComment(channels: readonly string[]): string {
  return `<!-- ${renderSiteGlueChannelsLine(channels)} -->`;
}

export function renderSiteGlueChannelsLlmsSection(channels: readonly string[]): string {
  const unique = uniqueChannelIds(channels);
  if (unique.length === 0) return "";
  return [
    "## SiteGlue Agent channels",
    "People and their AI agents can send a message on these channels and receive a SiteGlue Agent response.",
    "",
    renderSiteGlueChannelsLine(unique),
    ...unique.map((channel) => `- ${channel}`),
  ].join("\n");
}

export function parseAdvertisedAgentChannels(text: string | null | undefined): ChannelId[] {
  if (!text) return [];
  const tokens: string[] = [];
  for (const match of text.matchAll(HEADER_RE)) {
    for (const token of (match[1] ?? "").split(/[,|]/)) {
      if (token.trim()) tokens.push(token);
    }
  }
  return uniqueChannelIds(tokens);
}

export function extractSiteGlueWidgetIds(html: string | null | undefined): string[] {
  if (!html) return [];
  const found = new Set<string>();
  for (const pattern of [WIDGET_ID_RE, WIDGET_ID_FALLBACK_RE]) {
    pattern.lastIndex = 0;
    for (const match of html.matchAll(pattern)) {
      if (match[1]) found.add(match[1]);
    }
  }
  return [...found];
}

export function htmlHasSiteGlueWidget(html: string | null | undefined): boolean {
  return extractSiteGlueWidgetIds(html).length > 0;
}

export function detectAgentRespondingChannels(input: {
  html?: string | null;
  robots?: string | null;
  llms?: string | null;
}): ChannelId[] {
  const found = new Set<ChannelId>();
  for (const text of [input.robots, input.llms, input.html]) {
    for (const channel of parseAdvertisedAgentChannels(text)) found.add(channel);
  }
  if (htmlHasSiteGlueWidget(input.html)) found.add("website");
  return [...found];
}

export function agentChannelsFromProjectFlags(input: {
  flags?: ProjectChannelFlags;
  appointmentsMcpServerId?: string | null;
}): ChannelId[] {
  const flags = input.flags ?? {};
  const found = new Set<ChannelId>();
  if (flags.aiChat !== false) found.add("website");
  if (flags.call) found.add("phone");
  if (flags.sms) found.add("sms");
  if (flags.email) found.add("email");
  if (flags.talk) found.add("browser_call");
  if (flags.video) found.add("video");
  if (flags.whatsapp) found.add("whatsapp");
  if (flags.messenger) found.add("messenger");
  if (input.appointmentsMcpServerId && input.appointmentsMcpServerId !== "none") {
    found.add("scheduling");
  }
  return [...found];
}

export function uniqueChannelIds(channels: readonly string[]): ChannelId[] {
  const found = new Set<ChannelId>();
  for (const channel of channels) {
    const normalized = normalizeChannelToken(channel);
    if (!normalized) continue;
    for (const id of expandLegacyChannel(normalized)) found.add(id);
  }
  return [...found];
}

/** Older SiteGlue-Channels lines used a combined Social Media token. */
function expandLegacyChannel(id: ChannelId): ChannelId[] {
  if (id === "social") return ["whatsapp", "messenger"];
  return [id];
}

function normalizeChannelToken(value: string): ChannelId | null {
  const token = value.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
  if (!token) return null;
  const aliased = CHANNEL_ALIASES[token];
  if (aliased) return aliased;
  const underscored = token.replace(/\s+/g, "_");
  return isChannelId(underscored) ? underscored : null;
}
